import { useEffect, useState, type FocusEvent, type MouseEvent } from 'react'
import { createPortal } from 'react-dom'
import { getAllRestrictions, type HelpRestriction } from '../api/modelHelp'

/**
 * Щиты ограничений контента модели: до трёх слотов, заливка снизу вверх по
 * жёсткости (1/3, 2/3, полный = тема почти всегда под цензурой модели).
 * Ховер/фокус — тултип с заголовком и описанием (тексты из админки, раздел
 * «Инструкции нейросетей»). Пустые слоты (level 0) отсекает бэк.
 *
 * Тултип рендерится ПОРТАЛОМ в body с fixed-координатами: щиты живут внутри
 * строки селекта моделей, где и имя (ellipsis), и сам список режут absolute-
 * потомков overflow'ом — обычный тултип обрезался (проверено живьём).
 */
/** Щиты модели из общего бакета (один fetch на сессию) — обёртка «хук +
 *  рендер», чтобы вставлять в списки моделей без нарушения правил хуков
 *  (сквозное обозначение: селект, каталог, чат-док, виджет-пикер). */
export function ModelShields({ adapter, modelId, size = 14 }: { adapter?: string; modelId?: string; size?: number }) {
	const [items, setItems] = useState<HelpRestriction[]>([])
	useEffect(() => {
		let alive = true
		if (!adapter || !modelId) return
		void getAllRestrictions().then((map) => {
			if (alive) setItems(map.get(`${adapter}::${modelId}`) || [])
		})
		return () => {
			alive = false
		}
	}, [adapter, modelId])
	if (items.length === 0) return null
	return <RestrictionShields items={items} size={size} />
}

export default function RestrictionShields({ items, size = 14 }: { items: HelpRestriction[]; size?: number }) {
	const shown = (items || []).filter((r) => r.level >= 1 && r.level <= 3).slice(0, 3)
	const [tip, setTip] = useState<{ i: number; x: number; y: number } | null>(null)
	if (shown.length === 0) return null

	const show = (i: number) => (e: MouseEvent<HTMLElement> | FocusEvent<HTMLElement>) => {
		const r = e.currentTarget.getBoundingClientRect()
		setTip({ i, x: r.left + r.width / 2, y: r.top - 8 })
	}
	const hide = () => setTip(null)
	const active = tip ? shown[tip.i] : null

	return (
		<span className="rshields" aria-label="Ограничения контента модели">
			{shown.map((r, i) => (
				<span
					key={i}
					className="rshield"
					tabIndex={0}
					onMouseEnter={show(i)}
					onMouseLeave={hide}
					onFocus={show(i)}
					onBlur={hide}
				>
					<Shield level={r.level} size={size} />
				</span>
			))}
			{active &&
				createPortal(
					<span className="rshield__tip" role="tooltip" style={{ left: tip!.x, top: tip!.y }}>
						{active.title && <b className="rshield__tip-title">{active.title}</b>}
						{active.text && <span className="rshield__tip-text">{active.text}</span>}
					</span>,
					document.body,
				)}
		</span>
	)
}

function Shield({ level, size }: { level: number; size: number }) {
	// Симметричный контур щита в viewBox 24: остриё внизу, плечи сверху.
	const path = 'M12 2.6 L19.6 5.7 V11 C19.6 16.2 16.5 20.1 12 21.6 C7.5 20.1 4.4 16.2 4.4 11 V5.7 Z'
	// Заливка растёт снизу: y-срез по доле уровня от высоты глифа (2.6..21.6).
	const ratio = Math.max(0, Math.min(3, level)) / 3
	const fillTop = 21.6 - (21.6 - 2.6) * ratio
	// id клипа должен быть уникален на страницу — щитов на экране десятки
	const clipId = `rshield-${level}-${Math.round(size)}`
	return (
		<svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
			<defs>
				<clipPath id={clipId}>
					<path d={path} />
				</clipPath>
			</defs>
			<rect x="0" y={fillTop} width="24" height={21.6 - fillTop + 0.4} clipPath={`url(#${clipId})`} fill="currentColor" opacity="0.45" />
			<path d={path} stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
		</svg>
	)
}
